/* eslint-disable react-refresh/only-export-components -- a table of icon components, like shared/ui/icons */
import type { ReactNode } from 'react';

/** The editor toolbar's glyphs: one stroke style, so no button is a stray character. */
function Svg({ children }: { children: ReactNode }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {children}
    </svg>
  );
}

export const ToolIcons = {
  undo: () => (
    <Svg>
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
    </Svg>
  ),
  redo: () => (
    <Svg>
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H10a6 6 0 0 0 0 12h3" />
    </Svg>
  ),
  alignLeft: () => (
    <Svg>
      <path d="M4 6h16M4 12h10M4 18h14" />
    </Svg>
  ),
  alignCenter: () => (
    <Svg>
      <path d="M4 6h16M7 12h10M5 18h14" />
    </Svg>
  ),
  alignRight: () => (
    <Svg>
      <path d="M4 6h16M10 12h10M6 18h14" />
    </Svg>
  ),
  alignJustify: () => (
    <Svg>
      <path d="M4 6h16M4 12h16M4 18h16" />
    </Svg>
  ),
  bulletList: () => (
    <Svg>
      <path d="M9 6h11M9 12h11M9 18h11" />
      <path d="M4 6h.01M4 12h.01M4 18h.01" strokeWidth="3" />
    </Svg>
  ),
  orderedList: () => (
    <Svg>
      <path d="M10 6h10M10 12h10M10 18h10" />
      <path d="M4 5.5 5.5 5v3.5M4 18.5h2.5L4 15.5h2.5" strokeWidth="1.6" />
    </Svg>
  ),
  taskList: () => (
    <Svg>
      <rect x="3" y="4" width="5" height="5" rx="1" />
      <path d="m4 6.5 1 1 2-2.5" strokeWidth="1.6" />
      <path d="M12 6.5h9M12 17.5h9" />
      <rect x="3" y="15" width="5" height="5" rx="1" />
    </Svg>
  ),
  indent: () => (
    <Svg>
      <path d="M4 5h16M12 10h8M12 15h8M4 20h16" />
      <path d="m4 9 3 3-3 3" />
    </Svg>
  ),
  outdent: () => (
    <Svg>
      <path d="M4 5h16M12 10h8M12 15h8M4 20h16" />
      <path d="m7 9-3 3 3 3" />
    </Svg>
  ),
  spacing: () => (
    <Svg>
      <path d="M11 6h9M11 12h9M11 18h9" />
      <path d="M6 4v16m0 0-2.5-2.5M6 20l2.5-2.5M6 4 3.5 6.5M6 4l2.5 2.5" />
    </Svg>
  ),
  quote: () => (
    <Svg>
      <path d="M6 17h3l2-4V7H5v6h3M14 17h3l2-4V7h-6v6h3" />
    </Svg>
  ),
  codeBlock: () => (
    <Svg>
      <path d="m8 8-4 4 4 4M16 8l4 4-4 4M13.5 5l-3 14" />
    </Svg>
  ),
  inlineCode: () => (
    <Svg>
      <path d="m9 8-4 4 4 4M15 8l4 4-4 4" />
    </Svg>
  ),
  rule: () => (
    <Svg>
      <path d="M4 12h16" />
      <path d="M8 6h8M8 18h8" opacity=".45" />
    </Svg>
  ),
  table: () => (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M3 10h18M3 15h18M9 4v16M15 4v16" />
    </Svg>
  ),
  image: () => (
    <Svg>
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <circle cx="9" cy="10" r="1.6" />
      <path d="m21 16-5-5-8 8" />
    </Svg>
  ),
  attachment: () => (
    <Svg>
      <path d="m20 11-8.5 8.5a5 5 0 0 1-7-7L13 4a3.5 3.5 0 0 1 5 5l-8.5 8.5a2 2 0 0 1-3-3L14 7" />
    </Svg>
  ),
  link: () => (
    <Svg>
      <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
      <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
    </Svg>
  ),
  plus: () => (
    <Svg>
      <path d="M12 5v14M5 12h14" />
    </Svg>
  ),
  chevron: () => (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  ),
  more: () => (
    <Svg>
      <path d="M5 12h.01M12 12h.01M19 12h.01" strokeWidth="3" />
    </Svg>
  ),
  clear: () => (
    <Svg>
      <path d="M6 5h12M12 5l-2 14M5 19l14-14" />
    </Svg>
  ),
  paintLetter: () => (
    <Svg>
      <path d="m6 16 6-12 6 12M8.5 11h7" />
    </Svg>
  ),
  fontSize: () => (
    <Svg>
      <path d="M3 18 8 6l5 12M5 14h6M15 18l3-8 3 8M16 16h4" />
    </Svg>
  ),
};
